import React from 'react';
import { useAppSelector } from '../hooks';
import { selectIsAuthenticated } from '../containers/authSlice';
import { useRelationOptions } from '../hooks/useRelationOptions';
import { BelongsToPopulatedValue, PropertyDefinitionBelongsTo, PropertyDefinitionHas } from '../types';
import { Dialog, DialogTitle, Divider, DialogContent, List, ListItemButton, ListItemIcon, ListItemText, IconButton, ListItem, Typography, Collapse, DialogActions } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import { Link } from 'react-router-dom';

function entryIdListsEqual(a: string[], b: string[]): boolean {
	if (a.length !== b.length) {
		return false;
	}
	for (let i = 0; i < a.length; i++) {
		if (a[i] !== b[i]) {
			return false;
		}
	}
	return true;
}

export type RelationValue = Pick<BelongsToPopulatedValue, 'value' | 'label'>;

interface RelationDialogProps {
	def: PropertyDefinitionBelongsTo | PropertyDefinitionHas;
	values: RelationValue[];
	onNavigateToEntry: (entryId: string) => void;
	onAddEntryClick: (def: PropertyDefinitionBelongsTo | PropertyDefinitionHas, currentEntryIds: string[]) => void;
	onChangeValues: (def: PropertyDefinitionBelongsTo | PropertyDefinitionHas, nextValues: RelationValue[]) => void;
	open: boolean;
	onClose: () => void;
	disabled?: boolean;
}

export const EntryPropertyBelongsToBlockEditDialog = ({def, values, onNavigateToEntry, onAddEntryClick, onChangeValues, open, onClose, disabled}: RelationDialogProps) => {
	const [isSearchExpanded, setIsSearchExpanded] = React.useState(values.length === 0);

	const isAuthenticated = useAppSelector(selectIsAuthenticated);

	const [draftEntryIds, setDraftEntryIds] = React.useState<string[]>(() =>
		values.map((v) => v.value),
	);

	const initialEntryIdsWhenOpenRef = React.useRef<string[]>(values.map((v) => v.value));

	React.useEffect(() => {
		if (open) {
			const next = values.map((v) => v.value);
			initialEntryIdsWhenOpenRef.current = next;
			setDraftEntryIds(next);
		}
	}, [open, values]);

	const {
		options: allOptions,
		isLoading,
		allowMany,
		disableSelect,
		isEntryArchived,
		canDeselectEntry,
		labelForEntryId,
	} = useRelationOptions({ def, values, selectedEntryIds: draftEntryIds, enabled: open });

	const allNonSelectedOptions = allOptions.filter((option) => !draftEntryIds.includes(option.id));

	const handleSearchExpand = () => {
		setIsSearchExpanded(!isSearchExpanded);
	};

	const commitIfChangedAndClose = () => {
		if (!entryIdListsEqual(draftEntryIds, initialEntryIdsWhenOpenRef.current)) {
			onChangeValues(def, draftEntryIds.map((entryId) => ({ value: entryId, label: labelForEntryId(entryId) })));
		}
		onClose();
	};

	/** @deprecated - use links instead */
	const handleNavigate = (entryId: string) => {
		onNavigateToEntry(entryId);
		commitIfChangedAndClose();
	};

	const handlePin = (id: string) => {
		if (draftEntryIds.includes(id)) {
			return;
		}
		setDraftEntryIds((prev) => allowMany ? [...prev, id] : [id]);
	};

	const handleUnpin = (id: string) => {
		if (!canDeselectEntry(id)) {
			return;
		}
		setDraftEntryIds((prev) => prev.filter((entryId) => entryId !== id));
	};

	const USE_COLLAPSIBLE_UI = allOptions.length > 10;

	return (
		<Dialog
			open={open}
			onClose={() => {
				commitIfChangedAndClose();
			}}
			maxWidth="sm"
			fullWidth
		>
			<DialogTitle color="text.secondary" display="flex" justifyContent="space-between" alignItems="center" sx={{pl: 2, py: 1, pr: 2, userSelect: 'none'}}>
				Select {def.label}
				<IconButton aria-label="Close" color='primary' onClick={commitIfChangedAndClose}>
					<CloseIcon fontSize="small" />
				</IconButton>
			</DialogTitle>
			<Divider />
			<DialogContent sx={{p: 0}}>
				<List disablePadding>
					{USE_COLLAPSIBLE_UI ? <>
						{draftEntryIds.map((entryId) => {
							const archived = isEntryArchived(entryId);
							return (
								<ListItemButton
									key={entryId}
									onClick={archived || !canDeselectEntry(entryId) ? undefined : () => handleUnpin(entryId)}
								>
									{!disableSelect && (
										<ListItemIcon sx={archived ? { color: 'action.disabled' } : undefined}>
											{allowMany ? <CheckBoxIcon fontSize="small" /> : <RadioButtonCheckedIcon fontSize="small" />}
										</ListItemIcon>
									)}
									<ListItemText primary={labelForEntryId(entryId)} slotProps={{primary: {variant: 'body1', sx: {textDecoration: archived ? 'line-through' : 'none'}}}} />
									<IconButton onClick={(e) => { e.stopPropagation(); handleNavigate(entryId); }}><ArrowForwardIcon fontSize="small" /></IconButton>
								</ListItemButton>
							);
						})}
						{!draftEntryIds.length && <ListItem>
							<Typography variant='body1' color='text.secondary' sx={{fontStyle: 'italic'}}>
							None
							</Typography>
						</ListItem>}
						{!disabled && !disableSelect && <Divider />}
						{!disabled && !disableSelect && <ListItem disablePadding>
							<ListItemButton onClick={handleSearchExpand} sx={{pl:2, pr: 0, py:1}} component="button">
								<ListItemIcon> {isSearchExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}</ListItemIcon>
								<ListItemText primary='Select' slotProps={{primary: {variant: 'body1', color: 'text.secondary'}}} />
							</ListItemButton>
						</ListItem>}
						<Collapse in={isSearchExpanded}>
							{isLoading && (
								<ListItem>
									<Typography variant='body2' color='text.secondary' sx={{ px: 2, py: 1 }}>
									Loading entries…
									</Typography>
								</ListItem>
							)}
							{allNonSelectedOptions.map((option) => (
								<ListItemButton key={option.id} onClick={() => handlePin(option.id)}>
									<ListItemIcon>{allowMany ? <CheckBoxOutlineBlankIcon fontSize="small" /> : <RadioButtonUncheckedIcon fontSize="small" />}</ListItemIcon>
									<ListItemText primary={option.label} slotProps={{primary: {variant: 'body1', sx: {textDecoration: isEntryArchived(option.id) ? 'line-through' : 'none'}}}} />
									<IconButton onClick={(e) => { e.stopPropagation(); handleNavigate(option.id); }}><ArrowForwardIcon fontSize="small" /></IconButton>
								</ListItemButton>
							))}
							{!allNonSelectedOptions.length && <ListItem>
								<Typography variant='body1' color='text.secondary' sx={{fontStyle: 'italic'}}>
									No options available
								</Typography>
							</ListItem>
							}
						</Collapse>
					</> : <>
						{allOptions.map((option) => {
							const isSelected = draftEntryIds.includes(option.id);
							const archived = isEntryArchived(option.id);
							const checkboxDisabled = archived && isSelected;
							return(
								<ListItemButton
									key={option.id}
									onClick={
										!disabled && !disableSelect
											? () => {
												if (isSelected) {
													if (!archived && canDeselectEntry(option.id)) {
														handleUnpin(option.id);
													}
												} else {
													handlePin(option.id);
												}
											}
											: undefined
									}
								>
									{!disabled && !disableSelect && (
										<ListItemIcon sx={checkboxDisabled ? { color: 'action.disabled' } : undefined}>
											{allowMany ? (isSelected ? <CheckBoxIcon fontSize="small" /> : <CheckBoxOutlineBlankIcon fontSize="small" />) : (isSelected ? <RadioButtonCheckedIcon fontSize="small" /> : <RadioButtonUncheckedIcon fontSize="small" />)}
										</ListItemIcon>
									)}
									<ListItemText primary={option.label} slotProps={{primary: {variant: 'body1', sx: {textDecoration: archived ? 'line-through' : 'none'}}}} />
									<IconButton component={Link} to={`/entries/${option.id}`} onClick={(e) => { e.stopPropagation(); onClose(); }}><ArrowForwardIcon fontSize="small" /></IconButton>
								</ListItemButton>
							);})}
						{isLoading && (
							<ListItem>
								<Typography variant='body2' color='text.secondary' sx={{ px: 2, py: 1 }}>
									Loading entries…
								</Typography>
							</ListItem>
						)}
						{!allOptions.length && (!isAuthenticated || isLoading) && <ListItem>
							<Typography variant='body1' color='text.secondary' sx={{fontStyle: 'italic'}}>
								No options available
							</Typography>
						</ListItem>}
					</>}
				</List>
			</DialogContent>
			{!disabled && <Divider />}
			{!disabled && <DialogActions sx={{p: 0}}>
				<ListItem disablePadding>
					<ListItemButton onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
						e.stopPropagation();
						commitIfChangedAndClose();
						onAddEntryClick(def, draftEntryIds);
					}} sx={{pl:2, pr: 0, py:1}} component="button">
						<ListItemIcon><AddIcon fontSize="small" /></ListItemIcon>
						<ListItemText primary='Create new' slotProps={{primary: {variant: 'body1', color: 'text.secondary'}}} />
					</ListItemButton>
				</ListItem>
			</DialogActions>}
		</Dialog>
	);
};